'use client';

/**
 * @packageDocumentation
 * @category Component
 */

import { BomdisoftBacklink, Brand } from '@bomdisoft/components';
import { hubBacklinkHref } from '@bomdisoft/seo';
import { dataDeletionNavLabel, getLanguageFromLocale } from '@bomdisoft/translations';
import s from '@pieleaf/shared/footer/styles.module.css';
import { socialAccounts } from '@pieleaf/shared/social';
import {
  allProduceText,
  checkOrderText,
  cocoaText,
  cocoyamsText,
  contactText,
  faqTitle,
  footerColCompany,
  footerColLegal,
  footerColOrders,
  footerColProduce,
  footerCopyright,
  footerTagline,
  navCookiePolicy,
  navGdpr,
  navSecurity,
  palmsText,
  placeOrderText,
  plantainsText,
  privacy,
  terms,
} from '@pieleaf/translations';
import Link from 'next/link';
import { useParams } from 'next/navigation';

const XIcon = () => (
  <svg viewBox='0 0 24 24'>
    <title>X</title>
    <path d='M18.9 1.1h3.7l-8 9.2L24 22.9h-7.4l-5.8-7.6-6.7 7.6H.4l8.6-9.8L0 1.1h7.6l5.2 6.9 6.1-6.9zm-1.3 19.6h2L6.5 3.2H4.4L17.6 20.7z' />
  </svg>
);

const LinkedInIcon = () => (
  <svg viewBox='0 0 24 24'>
    <title>LinkedIn</title>
    <path d='M19 0H5C2.2 0 0 2.2 0 5v14c0 2.8 2.2 5 5 5h14c2.8 0 5-2.2 5-5V5c0-2.8-2.2-5-5-5zM8 19H5V9h3v10zM6.5 7.5a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zM19 19h-3v-5c0-1.4-.6-2-1.5-2s-1.5.6-1.5 2v5h-3V9h3v1.4c.4-.7 1.4-1.4 2.5-1.4 2 0 3.5 1.4 3.5 4V19z' />
  </svg>
);

const FacebookIcon = () => (
  <svg viewBox='0 0 24 24'>
    <title>Facebook</title>
    <path d='M22 12a10 10 0 1 0-11.6 9.9v-7H8v-2.9h2.4V9.8c0-2.4 1.4-3.7 3.6-3.7 1 0 2.1.2 2.1.2v2.3h-1.2c-1.2 0-1.5.7-1.5 1.5v1.8h2.6l-.4 2.9h-2.2v7A10 10 0 0 0 22 12z' />
  </svg>
);

const InstagramIcon = () => (
  <svg viewBox='0 0 24 24'>
    <title>Instagram</title>
    <path d='M12 2.2c3.2 0 3.6 0 4.9.1 1.2.1 1.8.3 2.2.4.6.2 1 .5 1.4.9.4.4.7.8.9 1.4.2.4.4 1 .4 2.2.1 1.3.1 1.7.1 4.9s0 3.6-.1 4.9c-.1 1.2-.3 1.8-.4 2.2-.2.6-.5 1-.9 1.4-.4.4-.8.7-1.4.9-.4.2-1 .4-2.2.4-1.3.1-1.7.1-4.9.1s-3.6 0-4.9-.1c-1.2-.1-1.8-.3-2.2-.4-.6-.2-1-.5-1.4-.9-.4-.4-.7-.8-.9-1.4-.2-.4-.4-1-.4-2.2-.1-1.3-.1-1.7-.1-4.9s0-3.6.1-4.9c.1-1.2.3-1.8.4-2.2.2-.6.5-1 .9-1.4.4-.4.8-.7 1.4-.9.4-.2 1-.4 2.2-.4 1.3-.1 1.7-.1 4.9-.1zm0 6.6a3.2 3.2 0 1 0 0 6.4 3.2 3.2 0 0 0 0-6.4zm0 5.3a2.1 2.1 0 1 1 0-4.2 2.1 2.1 0 0 1 0 4.2zm4.1-5.4a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0z' />
  </svg>
);

export const Footer = () => {
  const params = useParams();
  const langPrefix = typeof params.lang === 'string' ? params.lang : 'en';
  const language = getLanguageFromLocale(langPrefix);
  const year = new Date().getFullYear();
  const copyright = footerCopyright[language].replace('{year}', String(year));
  const link = (path: string) => `/${langPrefix}${path}`;

  return (
    <footer className={s.root} data-testid='site-footer'>
      <div className={s.top}>
        <div className={s.brandCol} data-testid='footer-col-brand'>
          <div className={s.brandLogo}>
            <Brand href={`/${langPrefix}`} size={22} testId='footer-brand' wordmarkSuffix='ieleaf' />
          </div>
          <p className={s.tagline}>{footerTagline[language]}</p>
          <div className={s.socialRow}>
            {socialAccounts.linkedin && (
              <a aria-label='LinkedIn' className={s.socialLink} data-prodantix='Open Pieleaf on LinkedIn' data-testid='footer-social-linkedin' href={socialAccounts.linkedin} rel='noopener noreferrer' target='_blank'>
                <LinkedInIcon />
              </a>
            )}
            {socialAccounts.x && (
              <a aria-label='X' className={s.socialLink} data-prodantix='Open Pieleaf on X' data-testid='footer-social-x' href={socialAccounts.x} rel='noopener noreferrer' target='_blank'>
                <XIcon />
              </a>
            )}
            {socialAccounts.instagram && (
              <a aria-label='Instagram' className={s.socialLink} data-prodantix='Open Pieleaf on Instagram' data-testid='footer-social-instagram' href={socialAccounts.instagram} rel='noopener noreferrer' target='_blank'>
                <InstagramIcon />
              </a>
            )}
            {socialAccounts.facebook && (
              <a aria-label='Facebook' className={s.socialLink} data-prodantix='Open Pieleaf on Facebook' data-testid='footer-social-facebook' href={socialAccounts.facebook} rel='noopener noreferrer' target='_blank'>
                <FacebookIcon />
              </a>
            )}
          </div>
        </div>

        <div className={s.col} data-testid='footer-col-produce'>
          <h2 className={s.colHeading}>{footerColProduce[language]}</h2>
          <ul className={s.colList}>
            <li>
              <Link className={s.link} data-prodantix='Browse cocoa' data-testid='footer-link-cocoa' href={link('/produce/cocoa')}>
                {cocoaText[language]}
              </Link>
            </li>
            <li>
              <Link className={s.link} data-prodantix='Browse cocoyam' data-testid='footer-link-cocoyam' href={link('/produce/cocoyam')}>
                {cocoyamsText[language]}
              </Link>
            </li>
            <li>
              <Link className={s.link} data-prodantix='Browse palm produce' data-testid='footer-link-palm' href={link('/produce/palm')}>
                {palmsText[language]}
              </Link>
            </li>
            <li>
              <Link className={s.link} data-prodantix='Browse plantain' data-testid='footer-link-plantain' href={link('/produce/plantain')}>
                {plantainsText[language]}
              </Link>
            </li>
            <li>
              <Link className={s.link} data-prodantix='Browse all produce' data-testid='footer-link-all-produce' href={link('/produce')}>
                {allProduceText[language]}
              </Link>
            </li>
          </ul>
        </div>

        <div className={s.col} data-testid='footer-col-orders'>
          <h2 className={s.colHeading}>{footerColOrders[language]}</h2>
          <ul className={s.colList}>
            <li>
              <Link className={s.link} data-prodantix='Place an order' data-prodantix-action='place_order' data-testid='footer-link-place-order' href={link('/orders')}>
                {placeOrderText[language]}
              </Link>
            </li>
            <li>
              <Link className={s.link} data-prodantix='Check an order status' data-prodantix-action='check_order' data-testid='footer-link-check-order' href={link('/orders')}>
                {checkOrderText[language]}
              </Link>
            </li>
          </ul>
        </div>

        <div className={s.col} data-testid='footer-col-company'>
          <h2 className={s.colHeading}>{footerColCompany[language]}</h2>
          <ul className={s.colList}>
            <li>
              <Link className={s.link} data-prodantix='Read the FAQ' data-testid='footer-link-faq' href={link('/faq')}>
                {faqTitle[language]}
              </Link>
            </li>
            <li>
              <a className={s.link} data-prodantix='Email Pieleaf' data-prodantix-action='contact_email' data-testid='footer-link-contact' href={`mailto:${socialAccounts.email}`}>
                {contactText[language]}
              </a>
            </li>
          </ul>
        </div>

        <div className={s.col} data-testid='footer-col-legal'>
          <h2 className={s.colHeading}>{footerColLegal[language]}</h2>
          <ul className={s.colList}>
            <li>
              <Link className={s.link} data-prodantix='Read the privacy policy' data-testid='footer-link-privacy' href={link('/privacy-policy')}>
                {privacy[language]}
              </Link>
            </li>
            <li>
              <Link className={s.link} data-prodantix='Read the terms of service' data-testid='footer-link-terms' href={link('/terms-of-service')}>
                {terms[language]}
              </Link>
            </li>
            <li>
              <Link className={s.link} data-prodantix='Read the security page' data-testid='footer-link-security' href={link('/security')}>
                {navSecurity[language]}
              </Link>
            </li>
            <li>
              <Link className={s.link} data-prodantix='Read the GDPR page' data-testid='footer-link-gdpr' href={link('/gdpr')}>
                {navGdpr[language]}
              </Link>
            </li>
            <li>
              <Link className={s.link} data-prodantix='Read the cookie policy' data-testid='footer-link-cookie-policy' href={link('/cookie-policy')}>
                {navCookiePolicy[language]}
              </Link>
            </li>
            <li>
              <Link className={s.link} data-prodantix='Read how to delete my data' data-testid='footer-link-data-deletion' href={link('/data-deletion')}>
                {dataDeletionNavLabel[language]}
              </Link>
            </li>
          </ul>
        </div>
      </div>

      <div className={s.bottom}>
        <span className={s.copyright} data-testid='footer-copyright'>
          {copyright}
        </span>
        <BomdisoftBacklink href={hubBacklinkHref('pieleaf', langPrefix)} />
        <div className={s.contactRow}>
          {socialAccounts.mobile && (
            <a className={s.contactLink} data-prodantix='Call Pieleaf' data-prodantix-action='contact_phone' data-testid='footer-phone' href={`tel:${socialAccounts.mobile.replace(/\s/g, '')}`}>
              {socialAccounts.mobile}
            </a>
          )}
          <a className={s.contactLink} data-prodantix='Email Pieleaf' data-prodantix-action='contact_email' data-testid='footer-email' href={`mailto:${socialAccounts.email}`}>
            {socialAccounts.email}
          </a>
        </div>
      </div>
    </footer>
  );
};
